import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { format } from "date-fns";
import LandingNav from "@/components/LandingNav";
import SEOHead from "@/components/SEOHead";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchBlogPosts, type BlogPost } from "@/lib/contentful";

const Blog = () => {
  const { search } = useLocation();
  const isPreview = new URLSearchParams(search).get("preview") === "true";
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBlogPosts(isPreview)
      .then(setPosts)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [isPreview]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Blog — Circle. by Dearestly"
        description="Thoughts on relationships, connection, and the art of showing up for the people who matter most."
        path="/blog"
      />
      <LandingNav />

      <main className="mx-auto max-w-4xl px-6 py-16">
        <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-4">Blog</h1>
        <p className="text-muted-foreground text-lg mb-12 max-w-xl">
          Thoughts on relationships, connection, and the art of showing up.
        </p>

        {loading && (
          <div className="grid gap-10 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-48 w-full rounded-xl" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <p className="text-destructive">Something went wrong: {error}</p>
        )}

        {!loading && !error && posts.length === 0 && (
          <p className="text-muted-foreground">No posts yet. Check back soon!</p>
        )}

        {!loading && !error && posts.length > 0 && (
          <div className="grid gap-10 md:grid-cols-2">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}${isPreview ? "?preview=true" : ""}`}
                className="group block space-y-4"
              >
                {post.featuredImage && (
                  <div className="overflow-hidden rounded-xl">
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="aspect-[3/2] w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <h2 className="font-serif text-xl text-foreground group-hover:text-primary transition-colors">
                    {post.title}
                  </h2>
                  {post.publishedDate && (
                    <p className="text-xs text-muted-foreground tracking-wide uppercase">
                      {format(new Date(post.publishedDate), "MMMM d, yyyy")}
                    </p>
                  )}
                  {post.snippet && (
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {post.snippet}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Blog;
